import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { useClipboard } from "@galaxy-io/dls/hooks/useClipboard";
import Field from "@galaxy-io/dls/inputs/Field";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import TextAreaInput, { TextAreaInputVariant } from "@galaxy-io/dls/inputs/TextAreaInput";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { type Prompt, promptKeySchema } from "@gtm/contracts/core/knowledge";
import { CopyIcon, CopySimpleIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DrawerList, DrawerStack } from "@/components/workspace/drawer-content";
import { PageHeader } from "@/components/workspace/page-header";
import { FeedViewport } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";

export function KnowledgeView() {
  const { params, update } = useUrlState();
  const { toast } = useToast();
  const clipboard = useClipboard();
  async function copyPrompt(prompt: Prompt) {
    if (await clipboard.copy(prompt.body)) {
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Prompt copied",
        description: "Ready to paste.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } else {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not copy prompt",
        description: "Select the prompt text and copy it manually.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    }
  }
  const [newPrompt, setNewPrompt] = useState<Prompt | null>(null);

  const search = params.get("q") || "";
  const selected = promptKeySchema.safeParse(params.get("prompt")).data;
  const prompts = useQuery({
    queryKey: ["knowledge-prompts"],
    queryFn: () => unwrap(apiClient().GET("/knowledge/prompts")),
  });
  const currentPrompt = prompts.data?.items.find((item) => item.key === selected);
  const prompt = useRetainedValue(currentPrompt);
  const retainedDraft = useRetainedValue(newPrompt);
  const term = search.trim().toLowerCase();
  const items = (prompts.data?.items || []).filter((item) =>
    [item.title, item.key, item.description, item.body].some((value) =>
      value.toLowerCase().includes(term),
    ),
  );
  return (
    <>
      <PageHeader
        title="Knowledge Center"
        actions={
          <Button
            label="New prompt"
            onClick={() =>
              setNewPrompt({
                key: "",
                title: "",
                description: "",
                body: "",
                updatedAt: null,
                updatedByName: null,
              })
            }
            size={ButtonSize.MEDIUM}
          />
        }
      />
      <BaseToolbar
        leadingActions={
          <Input
            aria-label="Search prompts"
            placeholder="Search prompts…"
            value={search}
            onChange={(event) => update({ q: event || null }, true)}
            type="search"
            size={InputSize.SMALL}
            label="Search prompts"
            hideLabel
          />
        }
      />
      <FeedViewport>
        <RecordTable
          label="Prompts"
          isLoading={prompts.isPending}
          error={prompts.error || undefined}
          onRetry={() => void prompts.refetch()}
          rowKeys={items.map((item) => item.key)}
          onRowClick={(key) => update({ prompt: key, promptEdit: null })}
          columns={[
            { key: "title", label: "Prompt", width: 200, pin: "left" },
            { key: "description", label: "Description", width: "auto" },
            { key: "state", label: "State", width: 120 },
            { key: "editor", label: "Updated by", width: 150 },
            { key: "updated", label: "Updated", width: 190 },
          ]}
          rows={items.map((item) => ({
            title: item.title,
            description: (
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                {item.description}
              </Text>
            ),
            state: (
              <Status
                value={item.body ? "active" : "paused"}
                label={item.body ? "Written" : "Empty"}
              />
            ),
            editor: item.updatedByName || "—",
            updated: item.updatedAt ? date(item.updatedAt, true) : "—",
          }))}
        />
      </FeedViewport>
      {retainedDraft && (
        <PromptEditor
          prompt={retainedDraft}
          open={!!newPrompt}
          onClose={() => setNewPrompt(null)}
          onCreated={(key) => {
            setNewPrompt(null);
            update({ prompt: key, promptEdit: null });
          }}
        />
      )}
      {/* Loading and missing records have their own controlled overlay. */}
      {
        <SurfaceDialog
          open={!!selected && !currentPrompt}
          drawer={params.get("promptEdit") !== "1"}
          title={params.get("promptEdit") === "1" ? "Edit prompt" : "Prompt"}
          onClose={() => update({ prompt: null, promptEdit: null })}
        >
          <QueryState
            error={prompts.error ?? (prompts.isPending ? null : "Prompt not found.")}
            loadingMessage="Loading prompt…"
            onRetry={() => void prompts.refetch()}
          />
        </SurfaceDialog>
      }
      {prompt && (
        <>
          <SurfaceDialog
            open={!!selected && !!currentPrompt}
            drawer
            title={prompt.title}
            headerDescription={prompt.description}
            onClose={() => update({ prompt: null, promptEdit: null })}
            headerActions={
              <>
                <Button
                  ariaLabel="Copy prompt"
                  tooltip="Copy prompt"
                  icon={CopyIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => void copyPrompt(prompt)}
                />
                <Button
                  ariaLabel="Duplicate"
                  tooltip="Duplicate"
                  icon={CopySimpleIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() =>
                    setNewPrompt({ ...prompt, key: "", title: `${prompt.title} — copy` })
                  }
                />
                <Button
                  ariaLabel="Edit"
                  tooltip="Edit"
                  icon={PencilSimpleIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => update({ promptEdit: "1" })}
                />
              </>
            }
          >
            <DrawerList>
              <DescriptionList
                items={[
                  { label: "Sharing", value: "Everyone in this workspace" },
                  {
                    label: "Updated",
                    value: prompt.updatedAt ? date(prompt.updatedAt, true) : "Never",
                    family: FontFamily.MONO,
                  },
                  { label: "Updated by", value: prompt.updatedByName || "—" },
                ]}
              />
            </DrawerList>
            {prompt.body ? (
              <SourceMarkdown content={prompt.body} breaks />
            ) : (
              <Text>Uses the built-in instructions.</Text>
            )}
          </SurfaceDialog>
          {
            <PromptEditor
              open={!!selected && params.get("promptEdit") === "1"}
              key={prompt.key}
              prompt={prompt}
              onClose={() => update({ promptEdit: null })}
            />
          }
        </>
      )}
    </>
  );
}
function PromptEditor({
  open,
  prompt,
  onClose,
  onCreated,
}: {
  open: boolean;
  prompt: Prompt;
  onClose: () => void;
  onCreated?: (key: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const client = useQueryClient();
  const { toast } = useToast();
  const [body, setBody] = useState(prompt.body);
  const [title, setTitle] = useState(prompt.title);
  const [description, setDescription] = useState(prompt.description);
  const changed =
    body !== prompt.body || title !== prompt.title || description !== prompt.description;
  const [busy, setBusy] = useState(false);
  useDialogReset(open, () => {
    setFormError(undefined);
    setBody(prompt.body);
    setTitle(prompt.title);
    setDescription(prompt.description);
    setBusy(false);
  });
  async function save() {
    setFormError(undefined);
    setBusy(true);
    try {
      let createdKey: string | undefined;
      if (prompt.key) {
        await unwrap(
          apiClient().PUT("/knowledge/prompts/{key}", {
            params: { path: { key: prompt.key } },
            body: { title, description, body },
          }),
        );
      } else {
        const created = await unwrap(
          apiClient().POST("/knowledge/prompts", { body: { title, description, body } }),
        );
        createdKey = created.key;
      }
      await client.invalidateQueries({ queryKey: ["knowledge-prompts"] });
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Prompt saved",
        description: `${title} is available to everyone in this workspace.`,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      if (createdKey && onCreated) onCreated(createdKey);
      else onClose();
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title={prompt.key ? `Edit ${prompt.title}` : "New prompt"}
      headerDescription="Shared instructions you can reuse whenever you need them."
      onClose={onClose}
      busy={busy}
      dirty={changed || (!prompt.key && !!(title || body || description))}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Save prompt"
            isLoading={busy}
            isDisabled={busy || !title.trim() || body.length > 20_000 || (!!prompt.key && !changed)}
            onClick={() => void save()}
          />
        </>
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <DrawerStack>
        <Field label="Name">
          <Input
            aria-label="Prompt name"
            value={title}
            onChange={(event) => setTitle(event)}
            disabled={busy}
            placeholder="Outbound email — Mitch"
          />
        </Field>
        <Field label="Description (optional)">
          <Input
            aria-label="Prompt description"
            value={description}
            onChange={(event) => setDescription(event)}
            disabled={busy}
            placeholder="Who this is for and when it is useful"
          />
        </Field>
        <TextAreaInput
          label="Guidance"
          value={body}
          onChange={setBody}
          minRows={18}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="Describe your product, who the sender is, the tone to use and what the email should ask for…"
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Shared with everyone in this workspace. Choose this prompt when drafting, or copy it to
          use elsewhere. No channel or event type is required. Up to 20,000 characters.
        </Text>
      </DrawerStack>
    </SurfaceDialog>
  );
}
